/**
 * @NOTE I had to create a custom Option component to enable dir="rtl" per option.
 * I have not yet created a custom Group component to do the same...
 *
 * @NOTE I removed the react-windowed-select (since we had an outstanding tech-debt
 * task to remove it) in the hopes that it would solve DDC-424. However, it seems it's
 * actually a problem with react-select itself (https://github.com/JedWatson/react-select/issues/3546)
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import ReactSelect from "react-select";
import { Props } from "react-select/src/Select";
import ReactSelectAsync, { Props as AsyncProps } from "react-select/async";
import { StylesConfig, styleFn } from "react-select/src/styles";
import { ValueType, OptionsType, ActionMeta } from "react-select/src/types";
import { SelectComponents } from "react-select/src/components";
import { OptionProps } from "react-select/src/components/Option";
import { MenuProps } from "react-select/src/components/Menu";

import i18next from "i18next";
import cn from "clsx";
import { InputCustomChangeEventHandler, TextDirection } from "../../types";
import {
  get,
  has,
  isEmpty,
  sortAlphabeticallyByPropFn,
  toArray
} from "../../utils/helpers";
import Menu from "./Menu";
import Option from "./Option";
import "./InputSelectCustom.css";
import { clearDQRForm } from "../../utils/dqePlaces";
export interface ReactSelectOptionObject {
  label: string;
  value: string | number | null;
  dir?: TextDirection;
}

export interface ReactSelectOptionGroupObject {
  label: string;
  options: ReactSelectOptionObject[];
  dir?: TextDirection;
}

export type ReactSelectOption =
  | ReactSelectOptionObject
  | ReactSelectOptionGroupObject;
export type ReactSelectOptions = ReactSelectOption[];
export type ReactSelectedOption = ReactSelectOption;
export type ReactSelectedOptions = ReactSelectedOption[];

export type ReactSelectActionMeta = ActionMeta;

export type ReactSelectChangeEventHandler = (
  value: ValueType<ReactSelectOption>,
  action: ActionMeta
) => void;

export type ReactSelectAsyncLoadOptionsHandler = (
  inputValue: string,
  callback: (options: OptionsType<ReactSelectOption>) => void
) => Promise<any> | void;

export type ReactSelectRenderMessage = (obj: {
  inputValue: string;
}) => string | null;

export interface InputSelectCustomProps {
  /** The ID of the input element. */
  id: string;
  /** The name of the input element. */
  name?: string;
  /** The current value of the input. */
  value: ValueType<ReactSelectOption> | any;
  /** The options to render within the select input. */
  options: ReactSelectOptions;
  /**
   * Sort the options alphabetically.
   *
   * @default true
   */
  sortOptionsAlphabetically?: boolean;
  /**
   * The message to display when the search has found no options.
   *
   * @default "No matches"
   */
  /** The option to put to the first position. */
  firstPositionOption?: string | null;
  emptyResultsMessage?: string | null;
  /** Placeholder label. */
  placeholder?: string;
  /** If the options are to be loaded asynchronously. */
  isAsync?: boolean;
  /** Callback to dynamically load options asynchronously. */
  loadOptions?: ReactSelectAsyncLoadOptionsHandler;
  /**
   * The message to display when the asynchronous data is loading.
   *
   * @default "Loading..."
   */
  loadingMessage?: string | null;
  /**
   * The language of the form field.
   */
  lang?: string;
  /**
   * Allow the user to easily clear the value.
   *
   * @default false
   */
  clearable?: boolean;
  /** Enable the search functionality. */
  searchable?: boolean;
  /** Allow user to select multiple values. */
  multiple?: boolean;
  /** Mark if the input is disabled. */
  disabled?: boolean;
  /** Extra classes to assign to the input. */
  className?: string;
  /** Event handler for when input is focused. */
  onFocus?: React.ReactEventHandler;
  /** Event handler for when input is blurred. */
  onBlur?: React.ReactEventHandler;
  /** Event handler for when input is changed. */
  onChange?: InputCustomChangeEventHandler;
  /** Event handler for when input is cleared. */
  onClear?: React.ReactEventHandler;
  /** Extra props to send to the ReactSelect component. */

  reactSelectProps?: Props<ReactSelectOption> | AsyncProps<ReactSelectOption>;
}

// We want the ReactSelect styles to be customisable from CSS, without lots
// of CSS managed by the JS itself.
const NOOP_STYLES: styleFn = () => ({});
const NOOP_RENDER_MSG: ReactSelectRenderMessage = () => null;
const NOOP_REACT_SELECT_STYLES: StylesConfig = {
  clearIndicator: NOOP_STYLES,
  container: NOOP_STYLES,
  control: NOOP_STYLES,
  dropdownIndicator: NOOP_STYLES,
  group: NOOP_STYLES,
  groupHeading: NOOP_STYLES,
  indicatorsContainer: NOOP_STYLES,
  indicatorSeparator: NOOP_STYLES,
  input: NOOP_STYLES,
  loadingIndicator: NOOP_STYLES,
  loadingMessage: NOOP_STYLES,
  // We'll use some of the computed styles, but also unset some so they
  // can be managed by CSS.
  menu: (provided: any) => {
    return {
      ...provided,
      backgroundColor: undefined,
      borderRadius: undefined,
      boxShadow: undefined,
      boxSizing: undefined,
      marginBottom: undefined,
      marginTop: undefined
    };
  },
  // @NOTE style the menu list separately
  // menuList: NOOP_STYLES,
  // @NOTE change the maximum height of the menu list
  // menuList: (provided: any) => {
  //   return {
  //     ...provided,
  //     maxHeight: "100px"
  //   };
  // },
  // @NOTE This one needs the calculated styles to position the menu in relation
  // to the control, so it's commented out.
  // menuPortal: NOOP_STYLES,
  multiValue: NOOP_STYLES,
  multiValueLabel: NOOP_STYLES,
  multiValueRemove: NOOP_STYLES,
  noOptionsMessage: NOOP_STYLES,
  option: NOOP_STYLES,
  placeholder: NOOP_STYLES,
  singleValue: NOOP_STYLES,
  valueContainer: NOOP_STYLES
};

const CUSTOM_COMPONENTS: Partial<SelectComponents<ReactSelectOption>> = {
  Option: Option as React.ComponentType<OptionProps<ReactSelectOption>>,
  Menu: Menu as React.ComponentType<MenuProps<ReactSelectOption>>
};

export function flattenOptions(
  options: ReactSelectOptions
): ReactSelectOptionObject[] {
  return (
    !!options &&
    options.reduce(
      (acc: ReactSelectOptionObject[], o: ReactSelectOption) =>
        // Flatten groups
        !!o &&
        has(o, "options") &&
        !!(o as ReactSelectOptionGroupObject).options
          ? acc.concat(
              flattenOptions((o as ReactSelectOptionGroupObject).options)
            )
          : acc.concat([o as ReactSelectOptionObject]),
      []
    )
  );
}

export function getSelectedOptions(
  value: any,
  options: ReactSelectOptions
): ReactSelectOptionObject[] {
  const checkValue: any[] = toArray(value);
  return flattenOptions(options).filter((o: ReactSelectOptionObject) =>
    checkValue.includes(o.value)
  );
}
export function checIfIdIsAddressCountry(id: string): boolean {
  return id === "addressCountry" ? true : false;
}

export default function InputSelectCustom({
  id,
  name,
  value,
  options,
  sortOptionsAlphabetically = true,
  firstPositionOption = null,
  emptyResultsMessage = "No matches",
  placeholder = "",
  lang,
  isAsync = false,
  loadOptions,
  loadingMessage = "Loading...",
  clearable = false,
  searchable = false,
  multiple = false,
  disabled = false,
  className,
  onFocus,
  onBlur,
  onChange,
  onClear,
  reactSelectProps
}: InputSelectCustomProps) {
  // Internal ref to the react-select instance
  const selectRef = useRef<any>(null);
  const selectOptions = useMemo(() => {
    // No need to sort
    if (!sortOptionsAlphabetically) {
      return options;
    }

    // Let the sorting ceremony commence!
    const _options = toArray(options);

    // Display any empty string or null options first
    const nullOptions = _options
      .filter(o => o.value === "" || o.value === null)
      .sort(sortAlphabeticallyByPropFn("label"));

    // Sort everything else after
    const restOptions = _options
      .filter(o => !nullOptions.includes(o))
      .sort(sortAlphabeticallyByPropFn("label"));

    if (firstPositionOption) {
      const getCurrentIndex = restOptions.findIndex(
        item => item.value === firstPositionOption
      );
      let currentObjectToReorder = restOptions.splice(getCurrentIndex, 1)[0];
      restOptions.unshift(currentObjectToReorder);
    }

    // Combine the two
    return [...restOptions, ...nullOptions];
  }, [options]);

  const isRtl = useMemo(() => !!lang && i18next.dir(lang) === "rtl", [lang]);

  const currentValues = useMemo(() => {
    const _currentValues = getSelectedOptions(value, options);
    return !isEmpty(_currentValues) ? _currentValues : null;
  }, [value, options]);

  // Event handlers
  const handleFocus: React.FocusEventHandler = useCallback(
    (event: React.FocusEvent) => {
      if (!!onFocus) {
        onFocus(event);
      }
    },
    [onFocus]
  );
  const handleBlur: React.FocusEventHandler = useCallback(
    (event: React.FocusEvent) => {
      if (!!onBlur) {
        onBlur(event);
      }
    },
    [onBlur]
  );
  const handleChange: ReactSelectChangeEventHandler = useCallback(
    (newValue, action) => {
      if (onChange) {
        let _newValue = newValue;

        if (newValue && !multiple) {
          _newValue =
            newValue instanceof Array ? newValue[0].value : newValue.value;
        }
        onChange(_newValue, name);

        // Fix issue on iOS where it loses focus of the react-select control input element
        if (
          action.action === "clear" &&
          selectRef.current &&
          selectRef.current.select
        ) {
          if (checIfIdIsAddressCountry(id)) {
            const formModel = clearDQRForm("");
            document.dispatchEvent(
              new CustomEvent("form.updateState", {
                detail: formModel
              })
            );
          }
          selectRef.current.select.focus();
        }
        if (
          action.action === "select-option" &&
          selectRef.current &&
          selectRef.current.select
        ) {
          if (checIfIdIsAddressCountry(id)) {
            const formModel = clearDQRForm(_newValue as any);
            document.dispatchEvent(
              new CustomEvent("form.updateState", {
                detail: formModel
              })
            );
          }
          selectRef.current.select.focus();
        }
      }
    },
    [onChange]
  );
  //const [focused, setFocused] = useState(false);
  /*const ref = useRef({
    mounted: false
  });*/
  const handleClear = useCallback<React.MouseEventHandler>(
    (event: React.MouseEvent) => {
      event.preventDefault();
      // if (ref.current && ref.current.mounted) {
      //  setFocused(false);
      //}
      if (onClear) {
        onClear(event);
      }
    },
    [onClear]
  );

  // ReactSelect wants these to be functions
  const renderNoOptionsMessage: ReactSelectRenderMessage = !!emptyResultsMessage
    ? (): string | null => emptyResultsMessage
    : NOOP_RENDER_MSG;
  const renderLoadingMessage: ReactSelectRenderMessage = !!loadingMessage
    ? (): string | null => loadingMessage
    : NOOP_RENDER_MSG;

  return (
    <div
      id={`input-select-custom-${id}`}
      className={cn(
        "input-select-custom",
        !!multiple && "is-multiple",
        !!clearable && "is-clearable",
        !!currentValues &&
          get(currentValues, "0.value") !== null &&
          "has-value",
        className
      )}
    >
      {isAsync && !!loadOptions ? (
        <ReactSelectAsync
          ref={selectRef}
          inputId={id}
          name={name}
          value={currentValues}
          options={selectOptions}
          placeholder={placeholder}
          loadOptions={loadOptions}
          loadingMessage={renderLoadingMessage}
          noOptionsMessage={renderNoOptionsMessage}
          isClearable={clearable}
          isSearchable={searchable}
          isMulti={multiple}
          isDisabled={disabled}
          isRtl={isRtl}
          classNamePrefix="input-select-custom"
          menuPortalTarget={document.body}
          menuPlacement="auto"
          menuShouldScrollIntoView={true}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onclear={handleClear}
          styles={NOOP_REACT_SELECT_STYLES}
          components={CUSTOM_COMPONENTS}
          {...reactSelectProps}
        />
      ) : (
        <ReactSelect
          ref={selectRef}
          inputId={id}
          name={name}
          value={currentValues}
          options={selectOptions}
          placeholder={placeholder}
          noOptionsMessage={renderNoOptionsMessage}
          isClearable={clearable}
          isSearchable={searchable}
          isMulti={multiple}
          isDisabled={disabled}
          isRtl={isRtl}
          classNamePrefix="input-select-custom"
          menuPortalTarget={document.body}
          menuPlacement="auto"
          menuShouldScrollIntoView={true}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onclear={handleClear}
          styles={NOOP_REACT_SELECT_STYLES}
          components={CUSTOM_COMPONENTS}
          {...reactSelectProps}
        />
      )}
    </div>
  );
}
